<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import CoachingForm from '../../form/CoachingForm.vue';
    import { useQueryGetProtocol } from '@/js/services/api/protocol/protocolQueries';
    import { calculateSectionPoints, createProtocolState } from '../../form/coachingForm.utils';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoLabel from '@/js/components/EchoLabel.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import ProtocolDetailInfo from '../detail/ProtocolDetailInfo.vue';
    import { useGetInteractions } from '@/js/hooks/interaction';
    import Accordion from '@/js/components/Accordion.vue';
    import TranscriptionVisualization from '@/js/components/interactions/transcription/TranscriptionVisualization.vue';
    import TranscriptionChat from '@/js/components/interactions/transcription/TranscriptionChat.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { useProtocolLock } from '@/js/composables/useProtocolLock';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import { useMutationDeleteProtocols } from '@/js/services/api/protocol/protocolQueries';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { TrashIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canAccessQA = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.Access
    );

    const canEditAiCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.EditAiCoach
    );

    const canEditManualCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.EditManualCoach
    );

    const canEditProtocol = computed(() => {
        if (!protocolData.value) return false;
        return protocolData.value.protocol.createdByAi
            ? canEditAiCoach.value
            : canEditManualCoach.value;
    });

    const props = defineProps<{
        id: string;
        containerRef: HTMLElement | null;
    }>();

    const width = ref<number>(0);

    watch(
        () => props.containerRef,
        (newVal) => {
            if (newVal) {
                width.value = newVal.clientWidth - 24;
            }
        },
        { immediate: true }
    );

    const { data: protocolData, isFetching: protocolIsFetching } = useQueryGetProtocol(props.id);

    const { lockReason } = useProtocolLock(() => protocolData.value?.protocol);

    const interactionId = computed(() => {
        if (
            !protocolData.value ||
            !protocolData.value.protocol ||
            !protocolData.value.protocol.interactionPartId
        ) {
            return [];
        }

        return [protocolData.value?.protocol.interactionPartId];
    });

    const { data: interactionData } = useGetInteractions(interactionId);

    const interaction = computed(() => {
        return interactionData.value?.interactions[0];
    });

    const protocol = computed(() => {
        if (protocolData.value && protocolData.value.protocol && protocolData.value.coachingForm) {
            return createProtocolState(
                protocolData.value.coachingForm,
                protocolData.value.protocol
            );
        }
        return null;
    });

    const points = computed(() => {
        if (protocol.value && protocol.value) {
            return calculateSectionPoints(protocol.value);
        }
        return null;
    });

    const deleteProtocols = useMutationDeleteProtocols();

    const onDeleteProtocol = async (protocolId: string) => {
        const confirm = useConfirmationDialog();

        const confirmed = await confirm.openDialog({
            title: __('delete-protocol'),
            message: __('delete-protocol-confirmation'),
            confirmText: __('delete-protocol'),
        });

        if (!confirmed) return;

        try {
            await deleteProtocols.mutateAsync({ protocolIds: [protocolId] });
        } catch (error) {
            console.error(error);
        }
    };

    const currentTime = ref<number>(0);
</script>
<template>
    <div class="flex relative">
        <div
            :style="{ width: width + 'px' }"
            class="w-full sticky top-0 left-3 border-1 border-gray-light rounded-[14px] p-7"
        >
            <div v-if="protocolIsFetching">
                <LoadingDots />
            </div>
            <div v-else-if="protocolData && protocol && points">
                <div class="flex justify-between items-center mb-4">
                    <div class="flex gap-3 items-center">
                        <h4 class="heading-sm">
                            {{
                                __('protocol-number-label', {
                                    number: protocolData.protocol.protocolNumber + '',
                                })
                            }}
                        </h4>

                        <EchoLabel v-if="!protocolData.protocol.readByAgent" variant="light">
                            {{
                                __('not-read-by-agent', { agent: protocolData.protocol.agentName })
                            }}
                        </EchoLabel>

                        <EchoLabel v-if="protocolData.protocol.createdByAi" variant="solid">
                            {{
                                protocolData.protocol.editedBy
                                    ? __('coached-with-ai-edited')
                                    : __('coached-with-ai')
                            }}
                        </EchoLabel>
                    </div>

                    <div class="flex gap-2">
                        <EchoButton
                            :route="`/quality-assurance/protocols/${protocolData.protocol.id}`"
                            variant="primary"
                        >
                            {{ __('open') }}
                        </EchoButton>
                        <EchoTooltip
                            v-if="canAccessQA && canEditProtocol"
                            :label="lockReason ?? ''"
                            :disabled="!lockReason"
                            :tabindex="lockReason ? 0 : undefined"
                        >
                            <EchoButton
                                variant="secondary"
                                :disabled="!!lockReason"
                                @click="onDeleteProtocol(protocolData.protocol.id)"
                            >
                                {{ __('delete') }}
                                <template #icon-right><TrashIcon /></template>
                            </EchoButton>
                        </EchoTooltip>
                    </div>
                </div>

                <div class="mb-5">
                    <ProtocolDetailInfo
                        :key="'protocol-detail-info' + protocolData.protocol.updatedAt"
                        :interaction="interaction"
                        :protocol="protocolData.protocol"
                        :editable="false"
                    />
                </div>

                <div v-if="interaction" class="mb-5 border border-gray-lightest rounded-md">
                    <Accordion>
                        <template #summary>
                            {{ __('show-interaction-content') }}
                        </template>
                        <template #summaryOpen>
                            {{ __('hide-interaction-content') }}
                        </template>
                        <template #default>
                            <div class="p-5">
                                <div class="mb-5 pb-2 border-b border-gray-lightest">
                                    <TranscriptionVisualization
                                        :interaction="interaction"
                                        @current-time-update="currentTime = $event"
                                    />
                                </div>

                                <TranscriptionChat
                                    :interaction="interaction"
                                    :current-time="currentTime"
                                />
                            </div>
                        </template>
                    </Accordion>
                </div>

                <CoachingForm :protocol-form-state="protocol" :point-map="points" :preview="true" />
            </div>
        </div>
    </div>
</template>
